// n02 Tun Nikohi — variant that renders the standalone HTML at
// /templates/nikoh.html (stormy-night nikoh ceremony — cream + gold +
// wine on a night-sky canvas, Cormorant Garamond + Pinyon Script +
// DM Mono, ~440px shell with sky-canvas grain and lightning flashes)
// inside a full-bleed iframe.
//
// The HTML's customizer expects ISO YYYY-MM-DD; the rest of the
// taklifla codebase keeps dates as DD.MM.YYYY. We convert in this
// wrapper so the HTML stays byte-for-byte what the designer authored.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/nikoh.html";

function ddmmyyyyToIso(d?: string): string | undefined {
  if (!d) return undefined;
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(d);
  if (!m) return d;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function NikohStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  // The customizer splits names on "&" and assigns to bride/groom. Most
  // of the catalog stores it as a single `names` string ("Sardor & Sevinch"),
  // so split here.
  if (t.names) {
    const parts = t.names.split(/\s*&\s*/);
    if (parts[0]) params.set("groom", parts[0].trim());
    if (parts[1]) params.set("bride", parts[1].trim());
  }
  const iso = ddmmyyyyToIso(t.date);
  if (iso) params.set("date", iso);
  if (t.time) params.set("time", t.time);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (ctx.slug) params.set("slug", ctx.slug);
  params.set("v", "2");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Tun Nikohi"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#0A0E1A",
      }}
    />
  );
}
